import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { clientesService } from '@/api/clientes';
import { useCartStore } from '@/store/cartStore';
import { useEmpresaStore } from '@/store/empresaStore';
import type { Cliente } from '@/types';
import AppHeader from '@/components/layout/AppHeader';
import styles from '@/styles/ui.module.css';

type SortOption =
  | 'last-order-date'
  | 'city-asc'
  | 'city-desc'
  | 'name-asc'
  | 'name-desc'
  | 'code-asc'
  | 'code-desc';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'last-order-date', label: 'Fecha ultimo pedido' },
  { value: 'city-asc', label: 'Ciudad A-Z' },
  { value: 'city-desc', label: 'Ciudad Z-A' },
  { value: 'name-asc', label: 'Nombre cliente A-Z' },
  { value: 'name-desc', label: 'Nombre cliente Z-A' },
  { value: 'code-asc', label: 'Codigo cliente ascendente' },
  { value: 'code-desc', label: 'Codigo cliente descendente' },
];

const getLastOrderDate = (cliente: Cliente) =>
  cliente.ultimoPedidoFecha || cliente.fechaUltimoPedido || cliente.ultimaFechaPedido || null;

const compareText = (a: string | null | undefined, b: string | null | undefined) =>
  (a || '').localeCompare(b || '', 'es', { sensitivity: 'base' });

export default function ClientesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState<SortOption>('code-asc');
  const [showSortOptions, setShowSortOptions] = useState(false);
  const setClienteId = useCartStore((state) => state.setClienteId);
  const empresaSeleccionada = useEmpresaStore((state) => state.empresaSeleccionada);

  const { data, isLoading } = useQuery({
    queryKey: ['clientes', searchQuery, empresaSeleccionada?.id],
    queryFn: () =>
      clientesService.getClientes({
        search: searchQuery,
        limit: 50,
        comisionistaId: empresaSeleccionada?.id,
      }),
  });

  const sortedClientes = useMemo(() => {
    const clientes = [...(data?.data || [])];

    clientes.sort((a, b) => {
      switch (selectedSort) {
        case 'last-order-date': {
          const aDate = getLastOrderDate(a);
          const bDate = getLastOrderDate(b);
          const aTime = aDate ? new Date(aDate).getTime() : 0;
          const bTime = bDate ? new Date(bDate).getTime() : 0;
          if (bTime !== aTime) {
            return bTime - aTime;
          }
          return compareText(a.nombre, b.nombre);
        }
        case 'city-asc': {
          const cityCompare = compareText(a.ciudad, b.ciudad);
          return cityCompare !== 0 ? cityCompare : compareText(a.nombre, b.nombre);
        }
        case 'city-desc': {
          const cityCompare = compareText(b.ciudad, a.ciudad);
          return cityCompare !== 0 ? cityCompare : compareText(a.nombre, b.nombre);
        }
        case 'name-asc':
          return compareText(a.nombre, b.nombre);
        case 'name-desc':
          return compareText(b.nombre, a.nombre);
        case 'code-desc':
          return compareText(b.codigo, a.codigo);
        case 'code-asc':
        default:
          return compareText(a.codigo, b.codigo);
      }
    });

    return clientes;
  }, [data?.data, selectedSort]);

  const selectedSortLabel = sortOptions.find((option) => option.value === selectedSort)?.label;

  const handleClienteClick = (clienteId: string) => {
    setClienteId(clienteId);
    navigate(`/clientes/${clienteId}`);
  };

  return (
    <div className={styles.page}>
      <AppHeader
        title="Mis Clientes"
        subtitle={empresaSeleccionada ? empresaSeleccionada.nombre : 'Selecciona una empresa para comenzar'}
      />

      <div
        className={`${styles.clientsFilterHeader} ${showSortOptions ? styles.clientsFilterHeaderExpanded : ''}`}
      >
        <div className={styles.searchRow}>
          <div className={styles.searchControlsRow}>
            <div className={styles.searchField}>
              <label className={styles.searchLabel}>Cliente</label>
              <div className={styles.searchFieldInner}>
                <input
                  className={styles.searchInput}
                  placeholder="Nombre, Ubicación, ..."
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.searchSortBlock}>
              <label className={styles.searchLabel}>Ordenar por</label>
              <div className={styles.searchSortControls}>
                <button
                  onClick={() => setShowSortOptions((current) => !current)}
                  className={styles.searchSelectButton}
                  type="button"
                >
                  {selectedSortLabel}
                  <span className={`material-symbols-outlined ${styles.chipIcon}`}>
                    {showSortOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  </span>
                </button>

                {showSortOptions && (
                  <div className={styles.searchOptionRow}>
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedSort(option.value);
                          setShowSortOptions(false);
                        }}
                        className={`${styles.searchOptionButton} ${selectedSort === option.value ? styles.searchOptionButtonActive : ''}`}
                        type="button"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.screenWrapper} ${styles.clientsScreenWrapper}`}>
        <section className={styles.clientList}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            sortedClientes.map((cliente) => (
              <div key={cliente.id} className={styles.clientRow}>
                <div
                  className={styles.clientCard}
                  onClick={() => handleClienteClick(cliente.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleClienteClick(cliente.id);
                    }
                  }}
                >
                  <div className={styles.clientInfo}>
                    <div className={styles.clientDetails}>
                      <p className={styles.clientTitle}>{cliente.nombre}</p>
                      <div className={styles.clientMeta}>
                        <span className={styles.clientCode}>{cliente.codigo}</span>
                        <span className="material-symbols-outlined">location_on</span>
                        <span>{cliente.ciudad}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`${styles.chevronIcon} material-symbols-outlined`}>
                    chevron_right
                  </span>
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}
